import { memo, useMemo } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { displayAccountLabel } from "@common/helpers/accounting";
import styles from "./AccountListCell.module.scss";

interface Props {
    accountIds: string[];
    accounts: Account[];
    showAccountNumbers: boolean;
}

/**
 * Shared read-only rendering for a cell backed by a list of account ids:
 * blank when empty, the account's label when exactly one, or the first
 * label plus a `+N` chip when more than one. Hovering the chip lists the
 * remaining labels. Extracted so `CashLedgerCell` and `AccrualLedgerCell`
 * share one renderer.
 */
export const AccountListCell: React.FC<Props> = memo(
    ({ accountIds, accounts, showAccountNumbers }) => {
        const resolvedAccounts = useMemo(() => {
            const labels: Array<{ id: string; label: string }> = [];
            for (const id of accountIds) {
                const account = accounts.find(
                    (candidate) => candidate.id === id,
                );
                if (!account) {
                    continue;
                }
                labels.push({
                    id,
                    label: displayAccountLabel(account, showAccountNumbers),
                });
            }
            return labels;
        }, [accountIds, accounts, showAccountNumbers]);

        const [firstAccount, ...restAccounts] = resolvedAccounts;

        if (!firstAccount) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell>
                <span className={styles.accounts}>
                    <span className={styles.label}>{firstAccount.label}</span>
                    {restAccounts.length > 0 && (
                        <Tooltip
                            bullets={restAccounts.map(
                                (account) => account.label,
                            )}
                        >
                            <span className={styles.extraChip}>
                                +{restAccounts.length}
                            </span>
                        </Tooltip>
                    )}
                </span>
            </ViewTableCell>
        );
    },
);
AccountListCell.displayName = "AccountListCell";
